import type { ValidateAddressRequest } from '../../../core/models/address-validate';
import type { ShipmentAddressesFormValue } from '../models/address-form.model';
import { mapShipmentAddressesToValidateRequest } from './map-shipment-addresses-to-validate-request';
import type { AddressSuggestionPresentation } from './map-validate-results-to-suggestions';

/** Snapshot of form values sent to POST /addresses/validate when validation was accepted. */
export type AddressValidationAcceptanceSnapshot = ValidateAddressRequest;

/**
 * Returns true when a prior acceptance included returnTo validation.
 */
export function acceptedSnapshotIncludedReturnAddress(
  acceptedSnapshot: AddressValidationAcceptanceSnapshot,
): boolean {
  return acceptedSnapshot.addresses.some((entry) => entry.userType === 'returnTo');
}

/**
 * Builds a comparable snapshot of validate-relevant address fields.
 */
export function buildAddressValidationAcceptanceSnapshot(
  formValue: ShipmentAddressesFormValue,
): AddressValidationAcceptanceSnapshot {
  return mapShipmentAddressesToValidateRequest(formValue);
}

/**
 * Returns true when the current form still matches a previously accepted validation snapshot.
 */
export function isAddressValidationAccepted(
  currentFormValue: ShipmentAddressesFormValue,
  acceptedSnapshot: AddressValidationAcceptanceSnapshot | null,
): boolean {
  if (!acceptedSnapshot) {
    return false;
  }

  const currentSnapshot = buildAddressValidationAcceptanceSnapshot(currentFormValue);

  if (
    currentFormValue.useDifferentReturnAddress &&
    !acceptedSnapshotIncludedReturnAddress(acceptedSnapshot)
  ) {
    return false;
  }

  return JSON.stringify(currentSnapshot) === JSON.stringify(acceptedSnapshot);
}

/**
 * Returns true when at least one suggested-address panel is still awaiting user action.
 */
export function hasActiveAddressSuggestions(
  shipFromSuggestion: AddressSuggestionPresentation | null,
  shipToSuggestion: AddressSuggestionPresentation | null,
  returnAddressSuggestion: AddressSuggestionPresentation | null,
): boolean {
  return Boolean(shipFromSuggestion ?? shipToSuggestion ?? returnAddressSuggestion);
}
